import React, { useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Box, Grid, Stack, Table, TableBody, TableCell, TableRow, Typography, Skeleton } from "@mui/material";
import $ from "jquery";
import colors from "../../../../utils/colors";
import V2PageHeader from "../../../common/V2PageHeader";
import { GreenTimeRangePicker } from "../../../common/Greens";

import { ReactComponent as ArrowUpIcon } from "../../../../vendor/_new/icon/overview/arrow_up.svg";
import { ReactComponent as ArrowDownIcon } from "../../../../vendor/_new/icon/overview/arrow_down.svg";
// import { ReactComponent as IssueIcon1 } from "../../../../vendor/_new/icon/overview/issue1.svg";
// import { ReactComponent as IssueIcon2 } from "../../../../vendor/_new/icon/overview/issue2.svg";
// import { ReactComponent as IssueIcon3 } from "../../../../vendor/_new/icon/overview/issue3.svg";
// import { ReactComponent as IssueIcon4 } from "../../../../vendor/_new/icon/overview/issue4.svg";
import { ReactComponent as DownloadIcon } from "../../../../vendor/_new/icon/overview/download.svg";

import DetectionBySeverity from "./components/DetectionBySeverity";
import DetectionBySeverityV2 from "./components/DetectionBySeverityV2";
import IncomingRequestTrends from "./components/IncomingRequestTrends";
import WAFDetectionInsight from "./components/WAFDetectionInsight";
import WorldVectorMap from "./components/WorldVectorMap";
import WebSecurityScore from "./components/WebSecurityScore";
// import { EventSeveritySankeyCard } from "./components/EventSeveritySankey";
import useHome from "../../../../hooks/user/useHome";
import useAuth from "../../../../hooks/useAuth";
import { formatBytes } from "../../../../utils/format";
import { getPercentageFor2Values, getSeverityFromThreatLevel, getSeverityName, formatPercentChange } from "../../../../utils/functions";
import {
  detectionPercentColor,
  isDetectionTrendUp,
  performancePercentColor,
} from "./overviewPercentUtils";
import { SeverityLevel, DetectionFilterCategory } from "../../../../utils/constants";
import { buildWafEventFiltersFromDashboardCard } from "../detection/dashboardEventNavigation";
import { Code, Storage, Security, BugReport, Language, VpnKey, Warning, Error as ErrorIcon, Block, Http, FolderOpen, PersonOff, Public, Terminal, Visibility } from "@mui/icons-material";

export const getLabelForLatestDetections = (label) => {
  let backgroundColor = label === "Critical" ? colors.red[10] : label === "Low" ? colors.neutral[10] : "#FFF6D0";
  let borderColor = label === "Critical" ? colors.red[100] : label === "Low" ? colors.neutral[400] : colors.yellow[200];
  let fontColor = label === "Critical" ? colors.red[100] : label === "Low" ? colors.neutral[700] : "#A68705";
  return (
    <Stack
      alignItems={"center"}
      justifyContent={"center"}
      sx={{ minWidth: 70, width: 70, height: 25, bgcolor: backgroundColor, borderRadius: "20px", border: `1px solid ${borderColor}` }}
    >
      <Typography sx={ { fontSize: 14, lineHeight: "21px", color: fontColor }}>{label}</Typography>
    </Stack>
  );
};

export const getIssueIcon = (type, title = "") => {
  const iconStyle = { width: 22, height: 22 };
  const titleLower = title?.toLowerCase() || "";

  // Specific attack intents first (Layerd-UI: folder-tree, user-x, terminal-square)
  if (
    titleLower.includes("path traversal") ||
    titleLower.includes("directory traversal") ||
    titleLower.includes("lfi") ||
    titleLower.includes("local file inclusion")
  ) {
    return <FolderOpen sx={{ ...iconStyle, color: "#F59E0B" }} />;
  }
  if (titleLower.includes("ssrf") || titleLower.includes("server-side request forgery")) {
    return <Public sx={{ ...iconStyle, color: "#3B82F6" }} />;
  }
  if (titleLower.includes("open redirect") || titleLower.includes("url redirect")) {
    return <Http sx={{ ...iconStyle, color: "#F59E0B" }} />;
  }
  if (
    titleLower.includes("server-side template") ||
    titleLower.includes("ssti") ||
    titleLower.includes("template injection")
  ) {
    return <Code sx={{ ...iconStyle, color: colors.red[100] }} />;
  }
  if (
    titleLower.includes("command injection") ||
    titleLower.includes("shell injection") ||
    titleLower.includes("os command") ||
    titleLower.includes("remote code execution") ||
    titleLower.includes("rce")
  ) {
    return <Terminal sx={{ ...iconStyle, color: colors.red[100] }} />;
  }
  if (
    titleLower.includes("access control") ||
    titleLower.includes("authorization") ||
    titleLower.includes("privilege") ||
    titleLower.includes("idor") ||
    titleLower.includes("broken access")
  ) {
    return <PersonOff sx={{ ...iconStyle, color: "#F59E0B" }} />;
  }
  if (
    titleLower.includes("information disclosure") ||
    titleLower.includes("data leak") ||
    titleLower.includes("sensitive data") ||
    titleLower.includes("exposure")
  ) {
    return <Visibility sx={{ ...iconStyle, color: "#F59E0B" }} />;
  }
  if (titleLower.includes("xss") || titleLower.includes("cross-site scripting")) {
    return <Code sx={{ ...iconStyle, color: colors.red[100] }} />;
  }
  if (titleLower.includes("sql") || titleLower.includes("sqli")) {
    return <Storage sx={{ ...iconStyle, color: colors.red[100] }} />;
  }
  if (titleLower.includes("injection")) {
    return <Storage sx={{ ...iconStyle, color: colors.red[100] }} />;
  }
  if (titleLower.includes("auth") || titleLower.includes("credential") || titleLower.includes("password") || titleLower.includes("token")) {
    return <VpnKey sx={{ ...iconStyle, color: "#F59E0B" }} />;
  }
  if (titleLower.includes("bot") || titleLower.includes("crawler") || titleLower.includes("spider")) {
    return <BugReport sx={{ ...iconStyle, color: "#8B5CF6" }} />;
  }
  if (titleLower.includes("geo") || titleLower.includes("country") || titleLower.includes("region")) {
    return <Language sx={{ ...iconStyle, color: "#3B82F6" }} />;
  }
  if (titleLower.includes("block") || titleLower.includes("denied") || titleLower.includes("forbidden")) {
    return <Block sx={{ ...iconStyle, color: colors.red[100] }} />;
  }
  if (titleLower.includes("http") || titleLower.includes("protocol")) {
    return <Http sx={{ ...iconStyle, color: "#10B981" }} />;
  }

  switch (type) {
    case SeverityLevel.CRITICAL:
      return <ErrorIcon sx={{ ...iconStyle, color: colors.red[100] }} />;
    case SeverityLevel.ERROR:
      return <ErrorIcon sx={{ ...iconStyle, color: "#F97316" }} />;
    case SeverityLevel.WARNING:
      return <Warning sx={{ ...iconStyle, color: "#F59E0B" }} />;
    case SeverityLevel.NOTICE:
      return <Security sx={{ ...iconStyle, color: "#3B82F6" }} />;
    case SeverityLevel.INFO:
      return <Security sx={{ ...iconStyle, color: colors.neutral[600] }} />;
    default:
      return <Security sx={{ ...iconStyle, color: colors.neutral[600] }} />;
  }
};


const NewOverviewPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, homeController, wafdashController, websiteController, wafeventController, planController, setHomeController } =
    useAuth();
  const {
    curProjectId,
    rows_per_page,
    from,
    sites,
    sigs,
    smartparses,
    regional_detections,
    top_detection_type,
    hostedscan_data,
    latestDetects,
    total_requests,
    total_bandwidth,
    setCurrentStatus,
    getSites,
    setErr,
  } = useHome();

  const [timeRange, setTimeRange] = useState("7d");
  const [customDateRange, setCustomDateRange] = useState(() => {
    const now = new Date();
    const oneDayAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const date = String(now.getDate()).padStart(2, "0");
    const year = String(now.getFullYear());
    const _month = String(oneDayAgo.getMonth() + 1).padStart(2, "0");
    const _date = String(oneDayAgo.getDate()).padStart(2, "0");
    const _year = String(oneDayAgo.getFullYear());
    return [_year + "-" + _month + "-" + _date + "T00:00:00", year + "-" + month + "-" + date + "T00:00:00"];
  });
  const [customRangeConfirm, setCustomRangeConfirm] = useState(false);

  const getTimeRangeValue = () => {
    if (timeRange === "custom" && customRangeConfirm) {
      const now = new Date();
      const timeZone =
        now.getTimezoneOffset() >= 0
          ? "-" + String(now.getTimezoneOffset() / 60).padStart(2, "0") + ":00"
          : "+" + String(now.getTimezoneOffset() / -60).padStart(2, "0") + ":00";
      return { time_zone: timeZone, from: customDateRange[0], to: customDateRange[1] };
    } else if (timeRange === "custom" && !customRangeConfirm) {
      return { period: "7d" };
    } else {
      return { period: timeRange };
    }
  };

  const selectPeriod = (period) => {
    if (period === "custom") {
      $(document).ready(function () {
        $(".react-minimal-datetime-range__range-input-wrapper").click();
        $(".react-minimal-datetime-range").css({
          "margin-top": $(".react-minimal-datetime-range__range-input-wrapper").parent().parent().parent().height() + 4,
        });
      });
    } else {
      setCustomRangeConfirm(false);
      setTimeRange(period);
    }
  };

  const selectCustomDateRange = (res) => {
    const normalizedRange = res;
    setCustomDateRange(normalizedRange);
    setCustomRangeConfirm(true);
    setTimeRange("custom");
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      getSites();
    }
    return () => setErr(null);
  }, [isAuthenticated, setErr, getSites]);

  useEffect(() => {
    const interval = setInterval(async () => {
      if (homeController) homeController.abort();
      if (wafdashController) wafdashController.abort();
      if (websiteController) websiteController.abort();
      if (wafeventController) wafeventController.abort();
      if (planController) planController.abort();
      if (sites?.length === 0) return;
      setCurrentStatus(setHomeController, "all", getTimeRangeValue(), rows_per_page, from, false);
    }, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeController, wafdashController, websiteController, wafeventController, planController, sites, setCurrentStatus, setHomeController, rows_per_page, from, timeRange, customDateRange, customRangeConfirm]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (sites === null) return;
    if (homeController) homeController.abort();
    if (wafdashController) wafdashController.abort();
    if (websiteController) websiteController.abort();
    if (wafeventController) wafeventController.abort();
    if (planController) planController.abort();
    setCurrentStatus(setHomeController, "all", getTimeRangeValue(), rows_per_page, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, sites, curProjectId, timeRange, customDateRange, customRangeConfirm]);

  const PerformanceItems = useMemo(
    () => ({
      "Total Request": {
        value: total_requests?.now || 0,
        percent: getPercentageFor2Values(total_requests?.past || 0, total_requests?.now || 0),
        change: total_requests?.now > total_requests?.past ? "up" : total_requests?.now < total_requests?.past ? "down" : "null",
      },
      "Inbound traffic": {
        value: formatBytes(total_bandwidth?.inbound?.now),
        percent: getPercentageFor2Values(total_bandwidth?.inbound?.past || 0, total_bandwidth?.inbound?.now || 0),
        change:
          total_bandwidth?.inbound?.now > total_bandwidth?.inbound?.past
            ? "up"
            : total_bandwidth?.inbound?.now < total_bandwidth?.inbound?.past
            ? "down"
            : "null",
      },
      "Outbound traffic": {
        value: formatBytes(total_bandwidth?.outbound?.now),
        percent: getPercentageFor2Values(total_bandwidth?.outbound?.past || 0, total_bandwidth?.outbound?.now || 0),
        change:
          total_bandwidth?.outbound?.now > total_bandwidth?.outbound?.past
            ? "up"
            : total_bandwidth?.outbound?.now < total_bandwidth?.outbound?.past
            ? "down"
            : "null",
      },
    }),
    [total_requests, total_bandwidth]
  );

  const DetectionTopIssueIdentifiedItems = useMemo(() => {
    return top_detection_type
      ? top_detection_type?.slice(0, 6)?.map((item) => {
          const rawTitle = item?.attack_type || item?.type;
          const title = !rawTitle || rawTitle.toLowerCase() === "unknown" 
            ? "Unclassified Detection" 
            : rawTitle;
          return {
            title,
            detection: item?.count,
            label: item?.severityLabel || getSeverityName(item?.severity),
            icon: item?.severity,
          };
        })
      : [];
  }, [top_detection_type]);

  const TopIssueIdentifiedItems = useMemo(() => {
    return (hostedscan_data?.data ?? []).slice(0, 4).map((item) => {
      return {
        title: item?.title,
        detection: 1,
        label: item?.threat_level?.charAt(0)?.toUpperCase() + item?.threat_level?.slice(1)?.toLowerCase(),
        icon: getSeverityFromThreatLevel(item?.threat_level),
      };
    });
  }, [hostedscan_data]);

  /** When vulnerability scan has no open findings, reuse live WAF / Smart Parse aggregates already on the page. */
  const useWafIssuesFallback = useMemo(() => {
    if (!Array.isArray(hostedscan_data?.critical)) return false;
    const rows = hostedscan_data?.data;
    if (Array.isArray(rows) && rows.length > 0) return false;
    const sumLast = (["critical", "high", "medium", "low"]).reduce((acc, k) => {
      const arr = hostedscan_data[k];
      if (!arr?.length) return acc;
      return acc + (arr[arr.length - 1]?.value ?? 0);
    }, 0);
    return sumLast === 0;
  }, [hostedscan_data]);

  const LatestDetectionItems = useMemo(() => {
    return [
      {
        value: latestDetects?.critical?.now || 0,
        percent: getPercentageFor2Values(latestDetects?.critical?.past || 0, latestDetects?.critical?.now || 0),
        label: "Critical",
      },
      {
        value: latestDetects?.high?.now || 0,
        percent: getPercentageFor2Values(latestDetects?.high?.past || 0, latestDetects?.high?.now || 0),
        label: "High",
      },
      {
        value: latestDetects?.medium?.now || 0,
        percent: getPercentageFor2Values(latestDetects?.medium?.past || 0, latestDetects?.medium?.now || 0),
        label: "Medium",
      },
      {
        value: latestDetects?.low?.now || 0,
        percent: getPercentageFor2Values(latestDetects?.low?.past || 0, latestDetects?.low?.now || 0),
        label: "Low",
      },
    ];
  }, [latestDetects]);

  const VulnerabilityAssessmentOpenIssuesItems = useMemo(() => {
    const hasPrior = (arr) => Array.isArray(arr) && arr.length > 1;
    const lastCritical =
      hostedscan_data?.critical && hostedscan_data?.critical.length > 0
        ? hostedscan_data?.critical[hostedscan_data?.critical.length - 1]?.value
        : 0;
    const previousCritical =
      hostedscan_data?.critical && hostedscan_data?.critical.length > 1
        ? hostedscan_data?.critical[hostedscan_data?.critical.length - 2]?.value
        : 0;
    const lastHigh =
      hostedscan_data?.high && hostedscan_data?.high.length > 0 ? hostedscan_data?.high[hostedscan_data?.high.length - 1]?.value : 0;
    const previousHigh =
      hostedscan_data?.high && hostedscan_data?.high.length > 1 ? hostedscan_data?.high[hostedscan_data?.high.length - 2]?.value : 0;
    const lastMedium =
      hostedscan_data?.medium && hostedscan_data?.medium.length > 0
        ? hostedscan_data?.medium[hostedscan_data?.medium.length - 1]?.value
        : 0;
    const previousMedium =
      hostedscan_data?.medium && hostedscan_data?.medium.length > 1
        ? hostedscan_data?.medium[hostedscan_data?.medium.length - 2]?.value
        : 0;
    const lastLow =
      hostedscan_data?.low && hostedscan_data?.low.length > 0 ? hostedscan_data?.low[hostedscan_data?.low.length - 1]?.value : 0;
    const previousLow =
      hostedscan_data?.low && hostedscan_data?.low.length > 1 ? hostedscan_data?.low[hostedscan_data?.low.length - 2]?.value : 0;
    return [
      {
        value: lastCritical,
        percent: hasPrior(hostedscan_data?.critical) ? getPercentageFor2Values(previousCritical, lastCritical) : null,
        label: "Critical",
      },
      {
        value: lastHigh,
        percent: hasPrior(hostedscan_data?.high) ? getPercentageFor2Values(previousHigh, lastHigh) : null,
        label: "High",
      },
      {
        value: lastMedium,
        percent: hasPrior(hostedscan_data?.medium) ? getPercentageFor2Values(previousMedium, lastMedium) : null,
        label: "Medium",
      },
      {
        value: lastLow,
        percent: hasPrior(hostedscan_data?.low) ? getPercentageFor2Values(previousLow, lastLow) : null,
        label: "Low",
      },
    ];
  }, [hostedscan_data]);

  const IssuesTopTableItems = useMemo(() => {
    if (useWafIssuesFallback) return DetectionTopIssueIdentifiedItems.slice(0, 4);
    return TopIssueIdentifiedItems;
  }, [useWafIssuesFallback, DetectionTopIssueIdentifiedItems, TopIssueIdentifiedItems]);

  const IssuesOpenCountItems = useMemo(() => {
    if (useWafIssuesFallback) return LatestDetectionItems;
    return VulnerabilityAssessmentOpenIssuesItems;
  }, [useWafIssuesFallback, LatestDetectionItems, VulnerabilityAssessmentOpenIssuesItems]);

  const handleTopWAFDetectionClick = (title) => {
    navigate("/v2/detection/waf_event", {
      state: {
        initialFilters: buildWafEventFiltersFromDashboardCard(DetectionFilterCategory.TopDetection, title),
        timeRange: getTimeRangeValue(),
      },
    });
  };

  return (
    <>
      <Helmet title="Overview" />
      <Box sx={{ p: 6 }}>
        <Stack spacing={6}>
          <Stack spacing={6}>
            <V2PageHeader
              title="Overview"
              sx={{ pb: 0 }}
              actions={(
                <Box sx={{ minWidth: { xs: "100%", md: "auto" } }}>
                  <GreenTimeRangePicker
                    selectPeriod={selectPeriod}
                    selectCustomDateRange={selectCustomDateRange}
                    timeRange={timeRange}
                    customDateRange={customDateRange}
                  />
                </Box>
              )}
            />
            {/* <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium" }}>
              Edge Ingress to Action Flow
            </Typography>
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  height: { xs: 340, sm: 380, md: 400 },
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <EventSeveritySankeyCard showTitle={false} />
              </Box>
            </Box> */}
            <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium" }}>
              Performance & severity
            </Typography>
            <Box sx={{ width: "100%" }}>
              <Grid container spacing={4}>
                <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                  <Stack sx={{ height: { xs: 280, sm: 300, md: 280 }, width: "100%", borderRadius: "8px", border: `1px solid ${colors.neutral[200]}` }}>
                    {Object.keys(PerformanceItems).map((key, index) => (
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        spacing={3}
                        key={index}
                        sx={{
                          px: 4,
                          py: 3,
                          height: 75,
                          borderBottom: index !== Object.keys(PerformanceItems).length - 1 ? `1px solid ${colors.neutral[200]}` : "none",
                        }}
                      >
                        {total_requests?.now === null ||
                        total_bandwidth?.inbound?.now === null ||
                        total_bandwidth?.outbound?.now === null ? (
                          <Stack sx={{ width: "80%" }}>
                            <Skeleton variant="text" sx={{ fontSize: 16, lineHeight: "24px", width: "80%" }} />
                            <Skeleton variant="text" sx={{ fontSize: 14, lineHeight: "21px", width: "80%" }} />
                          </Stack>
                        ) : (
                          <Stack>
                            <Typography
                              sx={{
                                fontSize: 16,
                                lineHeight: "24px",
                                fontWeight: "bold",
                                color: colors.neutral[1000],
                              }}
                            >
                              {PerformanceItems[key].value}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 14,
                                lineHeight: "21px",
                                fontWeight: "medium",
                                color: colors.neutral[700],
                              }}
                            >
                              {key}
                            </Typography>
                          </Stack>
                        )}
                        {total_requests?.now === null ||
                        total_bandwidth?.inbound?.now === null ||
                        total_bandwidth?.outbound?.now === null ? (
                          <Skeleton variant="circular" width={40} height={40} />
                        ) : (
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography
                              sx={{
                                fontSize: 14,
                                lineHeight: "21px",
                                color: performancePercentColor(PerformanceItems[key].percent),
                              }}
                            >
                              {formatPercentChange(PerformanceItems[key].percent)}
                            </Typography>
                            {PerformanceItems[key].change === "up" ? (
                              <ArrowUpIcon style={{ color: "#1DD75B" }} />
                            ) : PerformanceItems[key].change === "down" ? (
                              <ArrowDownIcon style={{ color: colors.red[100] }} />
                            ) : null}
                          </Stack>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                  <Stack sx={{ height: { xs: 280, sm: 300, md: 280 }, width: "100%", borderRadius: "8px", border: `1px solid ${colors.neutral[200]}` }}>
                    <Stack sx={{ px: 3, py: 2 }}>
                      <Typography
                        sx={{ fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                      >
                        Web security score (%)
                      </Typography>
                    </Stack>
                    <WebSecurityScore />
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                  <Stack sx={{ height: { xs: 280, sm: 300, md: 280 }, width: "100%", borderRadius: "8px", border: `1px solid ${colors.neutral[200]}` }}>
                    <Stack sx={{ px: 3, py: 2 }}>
                      <Typography
                        sx={{ fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                      >
                        Incoming request trends
                      </Typography>
                    </Stack>
                    <IncomingRequestTrends />
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                  <Stack sx={{ height: { xs: 280, sm: 300, md: 280 }, width: "100%", borderRadius: "8px", border: `1px solid ${colors.neutral[200]}` }}>
                    <Stack sx={{ px: 3, py: 2 }}>
                      <Typography
                        sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                      >
                        Latest detection
                      </Typography>
                    </Stack>
                    <Stack>
                      {LatestDetectionItems.map((item, index) => (
                        <Stack
                          key={index}
                          spacing={3}
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                          sx={{ px: 4, py: 2, height: 40 }}
                        >
                          {latestDetects === null ? (
                            <Skeleton variant="text" sx={{ fontSize: 14, lineHeight: "21px", width: "40%" }} />
                          ) : (
                            <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}>
                              {item.value} {item.value > 1 ? "issues" : "issue"}
                            </Typography>
                          )}
                          <Stack direction={"row"} spacing={5} alignItems={"center"}>
                            <Stack direction={"row"} alignItems={"center"}>
                              {latestDetects === null ? (
                                <Skeleton variant="text" sx={{ fontSize: 14, lineHeight: "21px", width: 50 }} />
                              ) : (
                                [
                                  <Typography
                                    key="percent"
                                    sx={{
                                      fontSize: 14,
                                      lineHeight: "21px",
                                      color: detectionPercentColor(item.percent),
                                    }}
                                  >
                                    {formatPercentChange(item.percent)}
                                  </Typography>,
                                  isDetectionTrendUp(item.percent) ? (
                                    <ArrowUpIcon key="arrow" style={{ color: colors.red[100], width: 20, height: 20 }} />
                                  ) : item.percent === 0 ? null : (
                                    <ArrowDownIcon key="arrow" style={{ color: "#1DD75B", width: 20, height: 20 }} />
                                  ),
                                ]
                              )}
                            </Stack>
                            {latestDetects === null ? (
                              <Skeleton variant="circular" width={25} height={25} />
                            ) : (
                              getLabelForLatestDetections(item.label)
                            )}
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Stack>
          <Stack spacing={6}>
            <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium" }}>
              Detections & insights
            </Typography>
            <Box sx={{ width: "100%" }}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Stack sx={{ height: 308, width: "100%", borderRadius: "8px", border: `1px solid ${colors.neutral[200]}` }}>
                    <Stack sx={{ px: 3, py: 2 }}>
                      <Typography
                        sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                      >
                        WAF detections by country
                      </Typography>
                    </Stack>
                    <WorldVectorMap regional_detections={regional_detections} />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack sx={{ height: 308, width: "100%", borderRadius: "8px", border: `1px solid ${colors.neutral[200]}` }}>
                    <Stack sx={{ px: 3, py: 2 }}>
                      <Typography
                        sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                      >
                        WAF detection insights
                      </Typography>
                    </Stack>
                    <WAFDetectionInsight sigs={sigs} smartparses={smartparses} />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack sx={{ height: 308, width: "100%", borderRadius: "8px", border: `1px solid ${colors.neutral[200]}` }}>
                    <Stack sx={{ px: 3, py: 2 }}>
                      <Typography
                        sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                      >
                        Detection by Severity
                      </Typography>
                    </Stack>
                    <Box sx={{ width: "100%", pr: 4, height: "100%" }}>
                      <Grid container spacing={6} sx={{ height: "100%" }}>
                        <Grid item xs={12} md={6}>
                          <DetectionBySeverity severity={SeverityLevel.CRITICAL} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DetectionBySeverity severity={SeverityLevel.ERROR} />
                        </Grid>
                      </Grid>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack sx={{ height: 308, width: "100%", borderRadius: "8px", border: `1px solid ${colors.neutral[200]}` }}>
                    <Stack sx={{ px: 3, py: 2 }}>
                      <Typography
                        sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                      >
                        Top WAF Detections
                      </Typography>
                    </Stack>
                    <Table sx={{ "& .MuiTableCell-body": { border: "none", height: 40, padding: 0 }, px: 3 }}>
                      <TableBody>
                        {top_detection_type === null
                          ? [1, 2, 3, 4, 5, 6]?.map((item, index) => (
                              <TableRow key={`Top_issues_identified_ske_${index}`}>
                                <TableCell width={"40%"}>
                                  <Skeleton variant="text" sx={{ fontSize: 14, lineHeight: "21px", width: "90%" }} />
                                </TableCell>
                                <TableCell width={"30%"}>
                                  <Skeleton variant="text" sx={{ fontSize: 14, lineHeight: "21px", width: "90%" }} />
                                </TableCell>
                                <TableCell width={"20%"}>
                                  <Skeleton variant="text" sx={{ fontSize: 14, lineHeight: "21px", width: "90%" }} />
                                </TableCell>
                                <TableCell align="right" width={"10%"}>
                                  <Stack alignItems={"flex-end"} sx={{ width: "100%" }}>
                                    <Skeleton variant="circular" width={24} height={24} />
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))
                          : DetectionTopIssueIdentifiedItems?.map((item, index) => (
                              <TableRow
                                key={`Top_issues_identified_${index}`}
                                hover
                                onClick={() => handleTopWAFDetectionClick(item.title)}
                                sx={{ cursor: "pointer" }}
                              >
                                <TableCell>
                                  <Typography
                                    sx={{
                                      fontSize: 14,
                                      lineHeight: "21px",
                                      color: colors.neutral[1000],
                                    }}
                                  >
                                    {item.title}
                                  </Typography>
                                </TableCell>
                                <TableCell>{getIssueIcon(item.icon, item.title)}</TableCell>
                                <TableCell>{getLabelForLatestDetections(item.label)}</TableCell>
                                <TableCell>
                                  <Typography
                                    sx={{
                                      fontSize: 14,
                                      lineHeight: "21px",
                                      color: colors.neutral[700],
                                    }}
                                  >
                                    {item.detection} Detection
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Stack alignItems={"flex-end"} sx={{ width: "100%" }}>
                                    <Box sx={{ cursor: "pointer", width: 24, height: 24 }}>
                                      <DownloadIcon style={{ color: colors.neutral[600] }} />
                                    </Box>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))}
                      </TableBody>
                    </Table>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Stack>
          <Stack spacing={6}>
            <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium" }}>Issues</Typography>
            <Box sx={{ width: "100%" }}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Stack sx={{ height: 225, width: "100%", borderRadius: "8px", border: `1px solid ${colors.neutral[200]}` }}>
                    <Stack sx={{ px: 3, py: 2 }}>
                      <Typography
                        sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                      >
                        {useWafIssuesFallback ? "Top runtime detections" : "Top issues identified"}
                      </Typography>
                    </Stack>
                    <Table sx={{ "& .MuiTableCell-body": { border: "none", height: 40, padding: 0 }, px: 3 }}>
                      <TableBody>
                        {hostedscan_data?.data === undefined
                          ? [1, 2, 3, 4]?.map((item, index) => (
                              <TableRow key={`Top_issues_identified_ske_${index}`}>
                                <TableCell width={"40%"}>
                                  <Skeleton variant="text" sx={{ fontSize: 14, lineHeight: "21px", width: "90%" }} />
                                </TableCell>
                                <TableCell width={"30%"}>
                                  <Skeleton variant="text" sx={{ fontSize: 14, lineHeight: "21px", width: "90%" }} />
                                </TableCell>
                                <TableCell width={"20%"}>
                                  <Skeleton variant="text" sx={{ fontSize: 14, lineHeight: "21px", width: "90%" }} />
                                </TableCell>
                                <TableCell align="right" width={"10%"}>
                                  <Stack alignItems={"flex-end"} sx={{ width: "100%" }}>
                                    <Skeleton variant="circular" width={24} height={24} />
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))
                          : IssuesTopTableItems?.length
                          ? IssuesTopTableItems.map((item, index) => (
                              <TableRow
                                key={`Top_issues_identified_${index}`}
                                hover={useWafIssuesFallback}
                                onClick={useWafIssuesFallback ? () => handleTopWAFDetectionClick(item.title) : undefined}
                                sx={useWafIssuesFallback ? { cursor: "pointer" } : undefined}
                              >
                                <TableCell>
                                  <Typography
                                    sx={{
                                      fontSize: 14,
                                      lineHeight: "21px",
                                      color: colors.neutral[1000],
                                    }}
                                  >
                                    {item.title}
                                  </Typography>
                                </TableCell>
                                <TableCell>{getIssueIcon(item.icon, item.title)}</TableCell>
                                <TableCell>
                                  {useWafIssuesFallback ? getLabelForLatestDetections(item.label) : null}
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    sx={{
                                      fontSize: 14,
                                      lineHeight: "21px",
                                      color: colors.neutral[700],
                                    }}
                                  >
                                    {item.detection} {useWafIssuesFallback ? "Detections" : "Detection"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Stack alignItems={"flex-end"} sx={{ width: "100%" }}>
                                    <Box sx={{ cursor: "pointer", width: 24, height: 24 }}>
                                      <DownloadIcon style={{ color: colors.neutral[600] }} />
                                    </Box>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))
                          : (
                              <TableRow>
                                <TableCell colSpan={5}>
                                  <Typography sx={ { fontSize: 14, color: colors.neutral[600], py: 1 }}>
                                    No vulnerability findings or runtime detections in this period.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                      </TableBody>
                    </Table>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Stack sx={{ height: 225, width: "100%", borderRadius: "8px", border: `1px solid ${colors.neutral[200]}` }}>
                    <Stack sx={{ px: 3, py: 2 }}>
                      <Typography
                        sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                      >
                        {useWafIssuesFallback ? "Runtime detections by severity" : "Vulnerability assessment open issues"}
                      </Typography>
                    </Stack>
                    <Stack>
                      {hostedscan_data?.critical === undefined ? (
                        <Table sx={{ "& .MuiTableCell-body": { border: "none", height: 40, padding: 0 }, px: 3 }}>
                          <TableBody>
                            {[1, 2, 3, 4]?.map((item, index) => (
                              <TableRow key={`Top_issues_identified_ske_${index}`}>
                                <TableCell width={"60%"}>
                                  <Skeleton variant="text" sx={{ fontSize: 14, lineHeight: "21px", width: "90%" }} />
                                </TableCell>
                                <TableCell width={"30%"}>
                                  <Skeleton variant="text" sx={{ fontSize: 14, lineHeight: "21px", width: "90%" }} />
                                </TableCell>
                                <TableCell align="right" width={"10%"}>
                                  <Stack alignItems={"flex-end"} sx={{ width: "100%" }}>
                                    <Skeleton variant="circular" width={24} height={24} />
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        IssuesOpenCountItems.map((item, index) => (
                          <Stack
                            key={index}
                            spacing={3}
                            direction={"row"}
                            alignItems={"center"}
                            justifyContent={"space-between"}
                            sx={{ px: 4, py: 2, height: 40 }}
                          >
                            <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}>
                              {item.value} {item.value > 1 ? "issues" : "issue"}
                            </Typography>
                            <Stack direction={"row"} spacing={5} alignItems={"center"}>
                              <Stack direction={"row"} alignItems={"center"}>
                                <Typography
                                  sx={{
                                    fontSize: 14,
                                    lineHeight: "21px",
                                    color: detectionPercentColor(item.percent),
                                  }}
                                >
                                  {item.percent === null && !useWafIssuesFallback ? "—" : formatPercentChange(item.percent)}
                                </Typography>
                                {item.percent === null && !useWafIssuesFallback ? null : isDetectionTrendUp(item.percent) ? (
                                  <ArrowUpIcon style={{ color: colors.red[100], width: 20, height: 20 }} />
                                ) : item.percent === 0 ? null : (
                                  <ArrowDownIcon style={{ color: "#1DD75B", width: 20, height: 20 }} />
                                )}
                              </Stack>
                              {getLabelForLatestDetections(item.label)}
                            </Stack>
                          </Stack>
                        ))
                      )}
                    </Stack>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Stack sx={{ height: 225, width: "100%", borderRadius: "8px", border: `1px solid ${colors.neutral[200]}` }}>
                    <Stack sx={{ px: 3, py: 2 }}>
                      <Typography
                        sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                      >
                        {useWafIssuesFallback ? "Detection trend (runtime)" : "Detection by Severity"}
                      </Typography>
                    </Stack>
                    {hostedscan_data?.critical === undefined ? (
                      <Table sx={{ "& .MuiTableCell-body": { border: "none", height: 53, padding: 0 }, px: 3 }}>
                        <TableBody>
                          {[1, 2, 3]?.map((item, index) => (
                            <TableRow key={`Top_issues_identified_ske_${index}`}>
                              <TableCell width={"25%"}>
                                <Skeleton variant="text" sx={{ fontSize: 14, lineHeight: "21px", width: "90%" }} />
                              </TableCell>
                              <TableCell width={"50%"}>
                                <Skeleton variant="text" sx={{ fontSize: 14, lineHeight: "21px", width: "90%" }} />
                              </TableCell>
                              <TableCell align="right" width={"25%"}>
                                <Skeleton variant="text" sx={{ fontSize: 14, lineHeight: "21px", width: "90%" }} />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Stack spacing={4} sx={{ mt: 2 }}>
                        <DetectionBySeverityV2
                          label="Critical"
                          timeseriesSource={useWafIssuesFallback ? "waf" : "hostedscan"}
                          percentChange={IssuesOpenCountItems.find((i) => i.label === "Critical")?.percent}
                        />
                        <DetectionBySeverityV2
                          label="High"
                          timeseriesSource={useWafIssuesFallback ? "waf" : "hostedscan"}
                          percentChange={IssuesOpenCountItems.find((i) => i.label === "High")?.percent}
                        />
                        <DetectionBySeverityV2
                          label="Medium"
                          timeseriesSource={useWafIssuesFallback ? "waf" : "hostedscan"}
                          percentChange={IssuesOpenCountItems.find((i) => i.label === "Medium")?.percent}
                        />
                      </Stack>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </Stack>
      </Box>
    </>
  );
};
export default NewOverviewPage;
